import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";

import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import FormErrors from "@/components/FormErrors/FormErrors.vue";
import { NamedRts } from "@/router";

import { TOAST_OPTIONS } from "@/constants";

import AutocompleteId from "@/components/Autocomplete/AutocompleteId.vue";
import { AutocompleteResult } from "@/components/Autocomplete/autocomplete-id";

import { sellContractsController } from "@/controllers/sellContracts";
import {
  buildFormSellContract,
  prettifySellContractStatus,
  SellContract,
} from "@/models/SellContract";
import { v4 as uuidv4 } from "uuid";

import {
  prettifyWeightQuantityPriceStatus,
  buildFormWeightQuantityPriceStatus,
} from "@/models/WeightQuantityPriceStatus";

import { buildFormCommodityAutocomplete } from "@/models/Commodity";
import { buildFormClientAutocomplete } from "@/models/Client";
import { buildFormLotUnitAutocomplete } from "@/models/LotUnit";

import { buildSubmitButton, buildFormErrors } from "@/utilities/form-helpers";

import { JSConn } from "@/utilities/js-conn";

export default defineComponent({
  name: "sellContractEditForm",
  components: {
    LoadingAnimation,
    AutocompleteId,
    FormErrors,
  },
  props: {
    sellContractId: {
      type: String,
      required: true,
    },
  },

  setup(props, ctx) {
    let $store = ctx.root.$store;

    let dataLoaded = ref(false);
    let loadingMessage = ref("Preparing the form...");

    let firstInput: any = ref(null);

    let deleteModal: any = ref(null);

    const showDeleteModal = () => {
      deleteModal.value.show();
    };

    const hideDeleteModal = () => {
      deleteModal.value.hide();
    };

    const deleteSellContract = () => {
      const jsConn = new JSConn();
      jsConn.formParams = {
        id: props.sellContractId,
      };
      sellContractsController.deleteSellContract(jsConn).then(() => {
        ctx.root.$router.push({
          name: NamedRts.sellContracts,
        });
        return Promise.resolve();
      });
    };

    const cancelParams = () => {
      if (props.sellContractId === "new") {
        return {
          name: NamedRts.sellContracts,
        };
      } else {
        return {
          name: NamedRts.sellContractShow,
          params: {
            sellContractId: props.sellContractId,
          },
        };
      }
    };

    let {
      formSellContract,
      sellContractStatuses,
      loadSellContractStatusesIntoForm,
      loadSellContractIntoForm,
    } = buildFormSellContract();

    const {
      defaultSellPriceStatuses,
      weightDefaultSellPriceStatuses,

      weightStatuses,
      quantityStatuses,

      loadWeightQuantityPriceStatusesIntoForm,
    } = buildFormWeightQuantityPriceStatus();

    loadWeightQuantityPriceStatusesIntoForm(quantityStatuses);
    loadWeightQuantityPriceStatusesIntoForm(defaultSellPriceStatuses);
    loadWeightQuantityPriceStatusesIntoForm(weightDefaultSellPriceStatuses);
    loadWeightQuantityPriceStatusesIntoForm(weightStatuses);

    let { submitButtonText, submitting } = buildSubmitButton();
    let { hasFormErrors, formErrors } = buildFormErrors();

    let { updateCommodity } = buildFormCommodityAutocomplete();
    let { updateClient } = buildFormClientAutocomplete();
    let { updateLotUnit } = buildFormLotUnitAutocomplete();

    const localUpdateCommodity = (evt: AutocompleteResult) => {
      updateCommodity(evt, formSellContract.formCommodity, formSellContract);
    };

    const localUpdateClient = (evt: AutocompleteResult) => {
      updateClient(evt, formSellContract.formClient, formSellContract);
    };

    const localUpdateLotUnit = (evt: AutocompleteResult) => {
      updateLotUnit(
        evt,
        formSellContract.formLotUnit,
        formSellContract,
        "lot_unit_id",
      );
    };

    const localUpdateWeightLotUnit = (evt: AutocompleteResult) => {
      updateLotUnit(
        evt,
        formSellContract.formLotUnit,
        formSellContract,
        "weight_lot_unit_id",
      );
    };

    const localUpdateAverageWeightLotUnit = (evt: AutocompleteResult) => {
      updateLotUnit(
        evt,
        formSellContract.formLotUnit,
        formSellContract,
        "average_weight_lot_unit_id",
      );
    };

    const commoditiesForAutocomplete = computed(() => {
      return $store.getters.commoditiesForAutocompleteId;
    });

    const clientsForAutocomplete = computed(() => {
      return $store.getters.clientsForAutocompleteId;
    });

    const lotUnitsForAutocomplete = computed(() => {
      return $store.getters.lotUnitsForAutocompleteId;
    });

    const weightLotUnitsForAutocomplete = computed(() => {
      return $store.getters.weightLotUnitsForAutocompleteId;
    });

    const apiSellContract = computed(() => {
      return $store.getters.sellContract as SellContract;
    });

    onMounted(() => {
      const jsConnCommodities = new JSConn();
      const jsConnClients = new JSConn();
      const jsConnLotUnits = new JSConn();
      const jsConnSellContract = new JSConn();

      loadSellContractStatusesIntoForm(sellContractStatuses);

      let dataPromises = [
        $store.dispatch("needCommodities", jsConnCommodities),
        $store.dispatch("needClients", jsConnClients),
        $store.dispatch("needLotUnits", jsConnLotUnits),
      ];

      if (props.sellContractId !== "new") {
        jsConnSellContract.formParams = {
          id: props.sellContractId,
        };
        dataPromises.push(
          sellContractsController.needSellContract(jsConnSellContract),
        );
      }

      Promise.all(dataPromises)
        .then(() => {
          // if the form is not a new form,
          // then the data needs to be loaded into the store
          if (props.sellContractId !== "new") {
            loadSellContractIntoForm(apiSellContract.value, formSellContract);
          }
        })
        .finally(() => {
          dataLoaded.value = true;
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
        });
    });

    const onSubmit = (evt: any) => {
      evt.preventDefault();
      const jsConn = new JSConn();

      hasFormErrors.value = false;
      formErrors.value = [];
      submitting.value = true;
      submitButtonText.value = "Submitting...";

      let apiResponse;
      if (props.sellContractId === "new") {
        formSellContract.id = uuidv4();
        jsConn.formParams = { ...formSellContract };
        apiResponse = sellContractsController.createSellContract(jsConn);
      } else {
        jsConn.formParams = { ...formSellContract };
        apiResponse = sellContractsController.updateSellContract(jsConn);
      }

      apiResponse
        .then(() => {
          ctx.root.$bvToast.toast(
            "Sell contract successfully saved",
            TOAST_OPTIONS,
          );
          ctx.root.$router.push(cancelParams());
        })
        .catch(() => {
          hasFormErrors.value = true;
          formErrors.value = jsConn.error["response"]["data"];
        })
        .finally(() => {
          submitting.value = false;
          submitButtonText.value = "Submit";
        });
    };

    return {
      dataLoaded,
      loadingMessage,
      formSellContract,
      sellContractStatuses,
      prettifySellContractStatus,

      commoditiesForAutocomplete,
      localUpdateCommodity,

      clientsForAutocomplete,
      localUpdateClient,

      lotUnitsForAutocomplete,
      weightLotUnitsForAutocomplete,
      localUpdateLotUnit,
      localUpdateWeightLotUnit,
      localUpdateAverageWeightLotUnit,

      onSubmit,
      submitting,
      submitButtonText,

      hasFormErrors,
      formErrors,

      firstInput,
      cancelParams,

      deleteModal,
      showDeleteModal,
      hideDeleteModal,
      deleteSellContract,

      defaultSellPriceStatuses,
      weightDefaultSellPriceStatuses,

      weightStatuses,
      quantityStatuses,

      prettifyWeightQuantityPriceStatus,
    };
  },
});
